import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Grid from '@mui/material/Grid';
import PEnterText from 'src/components/ModalDialogMUI6/styled/PEnterText';
import AutocompleteFieldFormik from 'src/components/AutocompleteFieldFormik';
import DivForm from 'src/components/ModalDialogMUI6/styled/DivForm';
import GridContainer from 'src/components/ModalDialogMUI6/styled/GridContainer';
import Spinner from 'src/components/Spinner/Spinner';
import helpers from 'src/helpers';

function FormSelectNumberToCall({
  contact,
  loading,
  onCloseModal,
  onSubmitForm = null,
  onSubmitFormRequest = () => {},
}) {
  const {
    id,
    phone1,
    phone2,
    phone3,
  } = contact || {};

  const phones = useMemo(
    () => (
      [
        phone1,
        phone2,
        phone3,
      ]
        .filter((phone) => !helpers.isEmpty(phone))
        .map((phone) => (
          {
            value: phone,
            label: helpers.transform.toPhoneNumber(phone),
          }
        ))
    ),
    [phone1, phone2, phone3]
  );

  const initialValues = {
    phoneNumber: phones?.[0] || '',
  };

  const validationSchema = Yup.object().shape({
    phoneNumber: Yup.object().shape({
      value: Yup.string().test('invalidPhone', 'Phone number is not valid', helpers.formValidation.getValidatePhone),
    })
      .nullable(false)
      .typeError('Please select a phone')
      .required('Please select a phone'),
  });

  const onSubmit = async (values, {
    resetForm,
    setStatus,
    setSubmitting
  }) => {
    if (onSubmitForm) {
      onSubmitForm(id, values, resetForm, setStatus, setSubmitting);
      return;
    }

    const doWhenTry = () => {
      resetForm();
      setStatus({ success: true });
      setSubmitting(false);
      onCloseModal('');
    };

    const doWhenCatch = () => {
      setStatus({ success: false });
      setSubmitting(false);
    };

    onSubmitFormRequest(id, values, doWhenTry, doWhenCatch);
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const {
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    touched,
    values,
    setFieldValue,
  } = formik || {};

  return (
    <form
      id="formSelectNumberToCall"
      onSubmit={handleSubmit}
    >
      <DivForm
        className="padding-top-bottom-v1 padding-left-right-v1"
      >
        <Spinner
          loading={loading}
          positionType="absolute"
        />
        <GridContainer
          className="first-item"
          rowSpacing={2}
        >
          <Grid
            xs={12}
            item
          >
            <PEnterText>
              This contact has more than one number.
              <br />
              Select the number you'd like to call.
            </PEnterText>
          </Grid>
        </GridContainer>
        <GridContainer
          rowSpacing={2}
        >
          <Grid
            xs={12}
            item
          >
            <AutocompleteFieldFormik
              setFieldValue={setFieldValue}
              touchedValue={touched.phoneNumber}
              errorValue={errors.phoneNumber}
              name="phoneNumber"
              handleChange={handleChange}
              handleBlur={handleBlur}
              values={values.phoneNumber}
              label="Phone Number"
              disabled={loading}
              options={phones || []}
              renderOption={helpers.formOptions.renderAutocompleteOption}
              isOptionEqualToValue={helpers.formOptions.isOptionEqualToValueV1}
              removeNameFromRenderInput
              required
              disableClearable
              slotProps={{
                input: {
                  readOnly: true,
                },
              }}
            />
          </Grid>
        </GridContainer>
      </DivForm>
    </form>
  );
}

FormSelectNumberToCall.propTypes = {
  contact: PropTypes.object.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  onSubmitForm: PropTypes.func,
  onSubmitFormRequest: PropTypes.func,
};

export default FormSelectNumberToCall;
